import { useQuery } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import {
  StaleNotice,
  UnresolvedState,
  counted,
  refreshFailed,
  shownState,
  unresolved,
} from '../ui/QueryStates';
import { useLanding } from '../ui/useLanding';
import { IconMail } from '../icons';
import { EMAIL_ANCHOR } from './emailView';

/**
 * "Asked not to hear from you": every address that unsubscribed from this shop's emails,
 * READ-ONLY. It lived on the Messages page until bulk messages went (MAP 249 ticket 02), and has
 * been a section of Settings → Email since ticket 07, under the anchor it already had
 * (`#asked-not-to-hear` lands on it and marks it).
 *
 * There is no button to put someone back on: that has to come from them. Its read is
 * `campaign:read`, which both roles Settings admits (owner and office) hold.
 */
export function AskedNotToHear() {
  const trpc = useTRPC();
  const q = useQuery(trpc.campaign.suppressions.queryOptions());
  const state = shownState(q, q.data);
  const landing = useLanding<HTMLDivElement>(EMAIL_ANCHOR.askedNotToHear);
  return (
    <div
      ref={landing.ref}
      id={EMAIL_ANCHOR.askedNotToHear}
      data-landed={landing.lit ? 'true' : undefined}
      className={`scroll-mt-[var(--sw-anchor-offset)] p-1 transition-colors ${landing.mark}`}
    >
      <h3 className="text-sm font-medium">
        {counted('Asked not to hear from you', state, q.data?.length ?? 0)}
      </h3>
      <p className="mb-2 text-xs text-muted-foreground">
        These addresses are left out of every promotional email automatically. They still
        get things about work they&apos;ve booked: quotes and invoices are not marketing.
        There is no button to put someone back on: that has to come from them.
      </p>
      {!q.data ? (
        <UnresolvedState
          state={unresolved(state)}
          what="who asked not to hear from you"
          icon={IconMail}
          error={q.error}
          onRetry={() => void q.refetch()}
          skeleton="h-10"
        />
      ) : (
        <>
          <StaleNotice show={refreshFailed(q, q.data)} onRetry={() => void q.refetch()} />
          {q.data.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nobody has asked yet.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {q.data.map((s) => (
                <li key={s.email} className="flex flex-wrap justify-between gap-2">
                  <span className="min-w-0 break-all">{s.email}</span>
                  <span className="text-muted-foreground">
                    {new Date(s.createdAt).toLocaleDateString()}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
